import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, StarIcon, ChevronRightIcon, ClockIcon, UsersIcon, VideoIcon } from 'lucide-react';
import { useWebinarSlot } from '../components/useWebinarSlot';
// Calculates the next webinar slot today (10:00, 13:00, 20:00) or tomorrow 10:00
function getNextSlot(now: Date): Date {
  const slots = [10, 13, 20];
  for (const h of slots) {
    const slot = new Date(now);
    slot.setHours(h, 0, 0, 0);
    if (slot.getTime() > now.getTime()) return slot;
  }
  // After 20:00 -> tomorrow 10:00
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(10, 0, 0, 0);
  return tomorrow;
}
function formatSlotLabel(slot: Date, now: Date): string {
  const sameDay = slot.getDate() === now.getDate() && slot.getMonth() === now.getMonth() && slot.getFullYear() === now.getFullYear();
  const hh = slot.getHours().toString().padStart(2, '0');
  return `${sameDay ? 'vandaag' : 'morgen'} om ${hh}:00`;
}
function spotsForMinutes(minutes: number): number {
  // 9 spots at 3hr+, decreasing by 1 every ~30 min, min 2
  const s = Math.floor(minutes / 30) + 3;
  return Math.max(2, Math.min(9, s));
}
function CountdownPlekken() {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const slot = getNextSlot(now);
  const diff = Math.max(0, slot.getTime() - now.getTime());
  const totalSec = Math.floor(diff / 1000);
  const hh = Math.floor(totalSec / 3600);
  const mm = Math.floor(totalSec % 3600 / 60);
  const ss = totalSec % 60;
  const minutesUntil = Math.floor(diff / 60000);
  const spots = spotsForMinutes(minutesUntil);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return <div className="relative mt-2" data-id="element-1108" data-mp="38lc2tbwsggub">
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-5 shadow-sm" data-id="element-1109" data-mp="33tkmdu7cjrhw">
        <div className="flex items-center justify-center gap-2 mb-4 pb-4 border-b border-amber-300" data-id="element-1110" data-mp="3mp9ncwhver28">
          <VideoIcon className="w-5 h-5 text-blue-900 flex-shrink-0" data-id="element-1111" data-mp="3row2jbh9ehdo" />
          <div className="text-center leading-tight" data-id="element-1112" data-mp="3dx7txi67yb1g">
            <div className="text-[10px] sm:text-[11px] uppercase tracking-wide text-blue-900/70 font-semibold" data-id="element-1113" data-mp="3dwpwii7tlqry">
              Gratis webinar
            </div>
            <div className="text-sm sm:text-base font-bold text-blue-900" data-id="element-1114" data-mp="33yihw6107kt4">
              Probleemgedrag bij honden
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6" data-id="element-1115" data-mp="38w1xb99fu3mn">
          <div className="flex items-center gap-2" data-id="element-1116" data-mp="2v0gclea8m601">
            <ClockIcon className="w-5 h-5 text-amber-700" data-id="element-1117" data-mp="2yz71wz8gojcl" />
            <div className="text-center" data-id="element-1118" data-mp="2z1s1fhlaen76">
              <div className="text-[11px] uppercase tracking-wide text-amber-800 font-semibold" data-id="element-1119" data-mp="t81vghfip5ko">
                Volgende sessie {formatSlotLabel(slot, now)}
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums leading-tight" aria-live="polite" data-id="element-1120" data-mp="2hq4qavlpdhvy">
                {pad(hh)}:{pad(mm)}:{pad(ss)}
              </div>
            </div>
          </div>

          <div className="hidden sm:block w-px h-12 bg-amber-300" data-id="element-1121" data-mp="2riahi344rva3" />

          <div className="flex items-center gap-2" data-id="element-1122" data-mp="2mo69dgj4ph7f">
            <UsersIcon className="w-5 h-5 text-red-600" data-id="element-1123" data-mp="31edjn8yzftx1" />
            <div className="text-center" data-id="element-1124" data-mp="31csobh4ug6y4">
              <div className="text-[11px] uppercase tracking-wide text-red-700 font-semibold" data-id="element-1125" data-mp="2wfspnrabq6h1">
                Nog beschikbaar
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-red-700 leading-tight" data-id="element-1126" data-mp="3aes7aezf5l3p">
                <span className="tabular-nums" data-id="element-1127" data-mp="35higauwdt1dr">{spots}</span>{' '}
                <span className="text-base font-semibold" data-id="element-1128" data-mp="1g0r8fu3vcy3m">plekken</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Playful hand-drawn arrow pointing to the button below */}
      <div className="absolute left-1/2 top-full -translate-x-1/2 pointer-events-none select-none" aria-hidden="true" data-id="element-1129" data-mp="1dft849du35vq">
        <div className="relative flex flex-col items-center pt-1" data-id="element-1130" data-mp="1hkj37w0o4gfl">
          <span className="text-amber-700 font-bold text-lg whitespace-nowrap" style={{
          fontFamily: "'Caveat', 'Comic Sans MS', cursive",
          transform: 'rotate(-6deg)'
        }} data-id="element-1131" data-mp="1miubw3f88kw8">
            klik hier!
          </span>
          <svg width="44" height="44" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-amber-600 animate-bounce -mt-1" style={{
          animationDuration: '1.6s'
        }} data-id="element-1132" data-mp="yt01rtmalafo">
            <path d="M 28 4 C 14 14, 42 26, 28 38" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-1133" data-mp="b1kvxqvb4azo" />
            <path d="M 20 32 L 28 42 L 36 32" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-1134" data-mp="8hfuym1wi5zw" />
          </svg>
        </div>
      </div>
    </div>;
}
function StickyCountdownBar({
  ctaHref
}: {
  ctaHref: string;
}) {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const slot = getNextSlot(now);
  const diff = Math.max(0, slot.getTime() - now.getTime());
  const totalSec = Math.floor(diff / 1000);
  const hh = Math.floor(totalSec / 3600);
  const mm = Math.floor(totalSec % 3600 / 60);
  const ss = totalSec % 60;
  const minutesUntil = Math.floor(diff / 60000);
  const spots = spotsForMinutes(minutesUntil);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-br from-amber-50 to-orange-50 border-t-2 border-amber-300 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" data-id="element-1135" data-mp="2ne1nic4vcnti">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-center gap-3 sm:gap-5" data-id="element-1136" data-mp="2sas3z0kl5lfg">
        {/* Webinar label */}
        <div className="hidden md:flex items-center gap-2 pr-4 border-r border-amber-300" data-id="element-1137" data-mp="2jbeuw90diyv9">
          <VideoIcon className="w-5 h-5 text-blue-900 flex-shrink-0" data-id="element-1138" data-mp="371ive7i9mii1" />
          <div className="leading-tight" data-id="element-1139" data-mp="3b3csy6x5z6dz">
            <div className="text-[10px] uppercase tracking-wide text-blue-900/70 font-semibold" data-id="element-1140" data-mp="3b2b6756scvgf">
              Gratis webinar
            </div>
            <div className="text-sm font-bold text-blue-900 leading-tight" data-id="element-1141" data-mp="2xa51cf932awa">
              Probleemgedrag bij honden
            </div>
          </div>
        </div>

        {/* Countdown + spots tightly grouped */}
        <div className="flex items-center gap-3 sm:gap-4" data-id="element-1142" data-mp="3urd88p2l472a">
          <div className="flex items-center gap-1.5" data-id="element-1143" data-mp="3uswnccci8c2h">
            <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700 flex-shrink-0" data-id="element-1144" data-mp="809bpxqs7s0v" />
            <div className="leading-tight" data-id="element-1145" data-mp="3zfo75h94f8i">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-wide text-amber-800 font-semibold" data-id="element-1146" data-mp="3yed5108jsqs">
                Volgende sessie
              </div>
              <div className="text-base sm:text-xl font-bold text-gray-900 tabular-nums leading-tight" aria-live="polite" data-id="element-1147" data-mp="cqzhhc34dww3">
                {pad(hh)}:{pad(mm)}:{pad(ss)}
              </div>
            </div>
          </div>

          <div className="w-px h-9 bg-amber-300 flex-shrink-0" data-id="element-1148" data-mp="3h829u9ayr126" />

          <div className="flex items-center gap-1.5" data-id="element-1149" data-mp="3v6a58cscpub8">
            <UsersIcon className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" data-id="element-1150" data-mp="3q6nnbq1kvpw3" />
            <div className="leading-tight" data-id="element-1151" data-mp="1af9knuen6qos">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-wide text-red-700 font-semibold" data-id="element-1152" data-mp="1ae6x9wgj36dm">
                Nog beschikbaar
              </div>
              <div className="text-base sm:text-xl font-bold text-red-700 leading-tight whitespace-nowrap" data-id="element-1153" data-mp="2zwdfrazrzzoj">
                <span className="tabular-nums" data-id="element-1154" data-mp="33xp8vqhuz31h">{spots}</span>{' '}
                <span className="text-xs sm:text-sm font-semibold" data-id="element-1155" data-mp="33vw5ck2jkyxp">
                  plekken
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <Link to={ctaHref} className="flex-shrink-0 bg-green-600 hover:bg-green-700 text-white text-center font-bold text-xs sm:text-sm px-3 sm:px-5 py-2.5 sm:py-3 rounded-full shadow-sm transition-colors whitespace-nowrap" data-id="element-1156" data-mp="3dpuyitkn18di">
          Gratis Aanmelden →
        </Link>
      </div>
    </div>;
}
export function ArtikelProbleemgedragAdv4VarKortPage() {
  const slotLabel = useWebinarSlot();
  const ctaHref = '/webinar-mc-adv4-pmax';
  const trustboxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if ((window as any).Trustpilot && trustboxRef.current) {
      ;
      (window as any).Trustpilot.loadFromElement(trustboxRef.current, true);
    }
  }, []);
  const CtaButton = ({
    children,
    className = ''
  }: {
    children: React.ReactNode;
    className?: string;
  }) => <Link to={ctaHref} className={`inline-block bg-green-600 hover:bg-green-700 text-white text-center font-semibold py-4 px-8 rounded-md shadow-sm transition-colors ${className}`} data-id="element-1157" data-mp="1kzakpuvy5fcg">
      {children}
    </Link>;
  return <div className="min-h-screen bg-white text-gray-900" style={{
    fontFamily: "'League Spartan', sans-serif"
  }} data-id="element-1158" data-mp="2dmx1k4u6qgh9">
      {/* Top navy banner */}
      <div className="bg-blue-900 text-white text-center py-3 text-sm font-medium tracking-wide" data-id="element-1159" data-mp="23p9437572uve">
        Stressless Dogs - Online Magazine
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 lg:py-10 pb-32" data-id="element-1160" data-mp="1znxciw2rfzkk">
        {/* Trustpilot row */}
        <div className="flex justify-center mb-6" data-id="element-1161" data-mp="sbyc6ommf825">
          <div ref={trustboxRef} className="trustpilot-widget" data-locale="nl-NL" data-template-id="5419b6ffb0d04a076446a9af" data-businessunit-id="65017f0060003ffc450ac45e" data-style-height="20px" data-style-width="100%" data-theme="light" data-id="element-1162" data-mp="o8icfjf98rl8">
            <a href="https://nl.trustpilot.com/review/stresslessdogs.nl" target="_blank" rel="noopener noreferrer" data-id="element-1163" data-mp="2oavu24gsurum">
              Trustpilot
            </a>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-3 lg:gap-10" data-id="element-1164" data-mp="2t8f7511rgykz">
          <article className="lg:col-span-2" data-id="element-1165" data-mp="2ta00wog94dhs">
            {/* Breadcrumb */}
            <nav className="text-sm text-gray-500 mb-4 flex items-center gap-1" data-id="element-1166" data-mp="377550iwen2ae">
              <span className="font-semibold text-gray-700" data-id="element-1167" data-mp="329n67lc3q3g4">Home</span>
              <ChevronRightIcon className="w-3 h-3" data-id="element-1168" data-mp="1cte1m14o5ene" />
              <span className="font-semibold text-gray-700" data-id="element-1169" data-mp="1ctw4vhpr0t4t">Trending</span>
              <ChevronRightIcon className="w-3 h-3" data-id="element-1170" data-mp="17vm50tt2x4cv" />
              <span data-id="element-1171" data-mp="3jhwe6wv76viu">Hondengedrag</span>
            </nav>

            {/* H1 */}
            <h1 className="text-3xl lg:text-4xl font-bold leading-tight mb-6" data-id="element-1172" data-mp="3jjzeqatz871t">
              Trekt, blaft, valt uit of negeert jouw hond je? Dit is waarom - en
              het ligt níet aan jou of je hond.
            </h1>

            {/* Subhead intro */}
            <p className="text-lg text-gray-700 leading-relaxed mb-6" data-id="element-1173" data-mp="3eq4p6qrowxut">
              Een Nederlandse gedragsdeskundige legt in een gratis online
              presentatie uit waarom <strong data-id="element-1174" data-mp="35q8757zt2x8q">72% van de baasjes</strong> denkt
              dat ze hun hond begrijpen - terwijl ze dagelijks cruciale signalen
              missen. Inmiddels volgden ruim{' '}
              <strong data-id="element-1175" data-mp="2vsbw0g6vc2am">100.000 hondenbaasjes</strong> deze presentatie.
            </p>

            {/* Hero figure */}
            <figure className="mb-8" data-id="element-1176" data-mp="pyu1r4lhjjxq">
              <img src="https://cdn.magicpatterns.com/uploads/w1qPqfcjJLeufAVhwWekKr/Scherm­afbeelding_2026-05-11_om_13.20.43.png" alt="Hondentrainer legt gedrag uit met whiteboard-tekeningen" className="w-full rounded-sm" data-id="element-1177" data-mp="pzvdlbabsagi" />
            </figure>

            <div className="space-y-5 text-[17px] leading-relaxed text-gray-800" data-id="element-1178" data-mp="1xxhlrpnc0a92">
              <p data-id="element-1179" data-mp="1ty0hgzjn5j5h">
                <strong data-id="element-1180" data-mp="1txh7q645a12j">Herken je dit?</strong> Je hond blaft om niks. Trekt aan
                de riem. Valt uit naar andere honden. Springt op tegen bezoek.
                En als je iets zegt, lijkt het alsof hij je niet hoort.
              </p>

              <p data-id="element-1181" data-mp="2hm9y7n1zzgpr">
                Het frustreert je. En het ergste? Je hebt het gevoel dat je iets
                verkeerd doet.
              </p>

              <p data-id="element-1182" data-mp="2cu8q8dbv4c5j">
                Maar het probleem ligt niet bij jou. En ook niet bij je hond.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-3" data-id="element-1183" data-mp="27v5trh9y7e96">
                Jullie spreken twee verschillende talen
              </h2>

              <p data-id="element-1184" data-mp="30l4ex1l0ur8k">
                Honden communiceren voor <strong data-id="element-1185" data-mp="30kcqcapudtrs">95%</strong> met hun lichaam en
                maar voor 5% met geluid. Bij mensen is dat precies andersom.
              </p>

              <p data-id="element-1186" data-mp="3cvdj9uj36xxh">
                Het gevolg: jij zegt met je mond "stop", maar je hond ziet met
                zijn ogen iets heel anders. De hele dag door, zonder dat je het
                doorhebt.
              </p>

              <p data-id="element-1187" data-mp="3lxmoq5zi0140">
                Een voorbeeld. Je hond springt op tegen bezoek. Jij duwt hem weg
                en roept "Laag!" Maar voor een opgewonden hond is wegduwen en
                roepen óók aandacht. Hij denkt niet: "ik word gestraft." Hij
                denkt: "kijk, mijn baasje doet mee."
              </p>

              <p data-id="element-1188" data-mp="4gvf43w9o2j5">
                En zo bouwt jouw hond keer op keer stress op. Hij voelt dat je
                boos bent, maar snapt niet waarom. Resultaat: meer blaffen, meer
                uitvallen, meer angst.
              </p>

              <figure className="my-6" data-id="element-1189" data-mp="ifle5p2ncca5">
                <img src="https://cdn.magicpatterns.com/uploads/64fjzy18K6oPyFW36hTnVW/Scherm­afbeelding_2026-05-11_om_12.39.25.png" alt="Eigenaar staat gefrustreerd tegenover hond in woonkamer" className="w-full rounded-sm" data-id="element-1190" data-mp="igmp7tjnwyrv" />
              </figure>

              <h2 className="text-2xl font-bold mt-8 mb-3" data-id="element-1191" data-mp="ei5u9cv1jd52">
                De alfa-mythe houdt het probleem in stand
              </h2>

              <p data-id="element-1192" data-mp="3hzeaxte186y7">
                Decennialang werd gezegd dat je "de baas" moest zijn. Maar{' '}
                <strong data-id="element-1193" data-mp="3dyc8l3otjes0">Dr. David L. Mech</strong> - de onderzoeker achter het
                alfa-concept - heeft zelf afstand genomen van die theorie. En
                recent onderzoek (<em data-id="element-1194" data-mp="3rvr6kwt50uvl">Casey et al., 2014</em>) laat zien dat
                dominantie-trainingen juist <strong data-id="element-1195" data-mp="3mxfusgja8aaq">méér</strong>{' '}
                gedragsproblemen veroorzaken.
              </p>

              <p data-id="element-1196" data-mp="179plnk1sjxi5">
                Gedragsdeskundige <strong data-id="element-1197" data-mp="1291jc2843u8m">Amee de Bruijn</strong>, oprichtster
                van Stressless Dogs, koos een andere weg. Ze leert baasjes de
                taal die honden onderling al spreken. Geen dwang. Geen
                correcties. Geen snoepjes als omkoping.
              </p>

              <p data-id="element-1198" data-mp="2b5gjk4ew29ks">
                Inmiddels hielp ze ruim <strong data-id="element-1199" data-mp="2b6i36x9z04e4">15.000 baasjes</strong>{' '}
                persoonlijk. Volgden <strong data-id="element-1200" data-mp="2g41im0iemn7n">100.000+</strong> mensen haar
                gratis online presentatie. En op Trustpilot staan meer dan{' '}
                <strong data-id="element-1201" data-mp="33vfbc7kgwkt5">1.000 5-sterren reviews</strong>.
              </p>

              <figure className="my-6" data-id="element-1202" data-mp="37vz7p8x7vke8">
                <img src="https://cdn.magicpatterns.com/uploads/tEawgTwN6ANqek5vk3uxJE/Scherm­afbeelding_2026-05-11_om_12.41.32.png" alt="700+ vijf-sterren reviews voor Amee van Stressless Dogs op Trustpilot" className="w-full rounded-sm" data-id="element-1203" data-mp="37uxv300jypue" />
              </figure>

              <div className="bg-gray-50 rounded-lg p-5 my-6" data-id="element-1204" data-mp="3rjq9h2ys46dq">
                <div className="flex gap-0.5 mb-2" data-id="element-1205" data-mp="3rl1boyckt62q">
                  {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-1206" data-mp="59jb9qrwqoci" />)}
                </div>
                <p className="italic text-gray-700 mb-2" data-id="element-1207" data-mp="3rfoetq3yv8du">
                  "Ik had al veel hondentrainingen gevolgd, maar mijn hond bleef
                  uitvallen. Met Liefdevol Communiceren begrijp ik nu eindelijk
                  wat ze wél en niet snapt."
                </p>
                <p className="text-sm font-semibold" data-id="element-1208" data-mp="3dojl14na5abf">- R. Bosma</p>
              </div>

              <h2 className="text-2xl font-bold mt-8 mb-3" data-id="element-1209" data-mp="38s2ts1ksafu4">
                Wat je in 90 minuten leert
              </h2>

              <ul className="space-y-3" data-id="element-1210" data-mp="33trnu1xm0ph6">
                {[<>
                    De <strong data-id="element-1211" data-mp="2ysc207nlyexq">5 basis-signalen</strong> die elke hond gebruikt
                  </>, <>
                    Hoe je <strong data-id="element-1212" data-mp="2utmevncrpj0w">stress herkent</strong> vóórdat het escaleert
                  </>, <>
                    Direct toepasbare technieken -{' '}
                    <strong data-id="element-1213" data-mp="1e989r7folnaq">zonder commando's of correcties</strong>
                  </>].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1214" data-mp="1j832b13gaq81" data-mpi={i}>
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center mt-0.5" data-id="element-1215" data-mp="1o8gz1mzlqdqr">
                      <CheckIcon className="w-4 h-4" data-id="element-1216" data-mp="vkbdiy0q4kpv" />
                    </span>
                    <span data-id="element-1217" data-mp="vi8qmmuvb7pd">{item}</span>
                  </li>)}
              </ul>

              <p className="mt-6" data-id="element-1218" data-mp="10hbkdb5edri1">
                Per sessie zijn er <strong data-id="element-1219" data-mp="15aeebke372yr">75 plekken</strong> beschikbaar. Live
                niet mogelijk? Alle deelnemers krijgen de opname automatisch
                toegestuurd.
              </p>

              <div className="my-8 flex flex-col items-center gap-24" data-id="element-1220" data-mp="ctafqdbcav7w">
                <div className="w-full" data-id="element-1221" data-mp="crh92y0q8azw">
                  <CountdownPlekken data-id="element-1222" data-mp="32b42ls4fdi23" />
                </div>
                <CtaButton className="w-full sm:w-auto" data-id="element-1223" data-mp="32dqfm7a34prp">
                  Bekijk beschikbaarheid online presentatie →
                </CtaButton>
              </div>
            </div>
          </article>

          {/* Sticky sidebar */}
          <aside className="hidden lg:block lg:col-span-1" data-id="element-1224" data-mp="36d7mn555tuyz">
            <div className="sticky top-6 bg-white border border-gray-200 rounded-lg p-6 shadow-sm" data-id="element-1225" data-mp="36ftsn0cek2f3">
              <p className="text-center font-bold text-lg mb-2" data-id="element-1226" data-mp="3gboiogiygzrw">Aanrader:</p>
              <div className="flex justify-center gap-0.5 mb-2" data-id="element-1227" data-mp="3gbeu8xdk70ob">
                {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-1228" data-mp="3u4wbucmoq3bp" />)}
              </div>
              <p className="text-center text-sm text-gray-600 mb-5" data-id="element-1229" data-mp="7w4wu2km6jmv">
                <strong data-id="element-1230" data-mp="2y3b19vt067p">4.68</strong> | 1000+ Geverifieerde Reviews
              </p>

              <h3 className="font-bold text-lg leading-tight mb-4 text-center" data-id="element-1231" data-mp="mial16jq16o7">
                Gratis Online Presentatie: Communiceren met je hond
              </h3>

              <ul className="space-y-3 mb-5" data-id="element-1232" data-mp="cke9weqsabq3">
                {['Waarom je hond blaft, trekt, uitvalt, opspringt of angstig is', 'Wat de nieuwste wetenschap zegt over hondentraining (meeste baasjes weten dit niet)', 'Hoe je gedragsproblemen oplost, zonder straffen of corrigeren', "Hoe je de 'Verborgen Handleiding' van je hond vindt en zo beter samenwerkt dan ooit"].map((item, i) => <li key={i} className="flex items-start gap-2 text-sm" data-id="element-1233" data-mp="3px6askez8fs5" data-mpi={i}>
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center mt-0.5" data-id="element-1234" data-mp="3uyvk9vmjwle4">
                      <CheckIcon className="w-3 h-3" data-id="element-1235" data-mp="3l06cwwsa6jc4" />
                    </span>
                    <span data-id="element-1236" data-mp="3l2adp5uvv286">{item}</span>
                  </li>)}
              </ul>

              <p className="text-sm text-center mb-4" data-id="element-1237" data-mp="2b99r5itoy91m">
                <strong data-id="element-1238" data-mp="2g7b7hvzqfu9e">
                  {slotLabel.dayPrefix} {slotLabel.dateLabel}
                </strong>{' '}
                om <strong data-id="element-1239" data-mp="22834st4d240g">{slotLabel.time}</strong>
              </p>

              <div className="rounded-lg overflow-hidden mb-4 aspect-video bg-gray-100" data-id="element-1240" data-mp="267byjxzei1w0">
                <img src="https://cdn.magicpatterns.com/uploads/o5oXcxyFnv65igmDcbYHEc/Scherm­afbeelding_2026-05-11_om_11.52.59.png" alt="Webinar preview" className="w-full h-full object-cover" data-id="element-1241" data-mp="2tx6amdbwbmf7" />
              </div>

              <Link to={ctaHref} className="block w-full bg-green-600 hover:bg-green-700 text-white text-center font-bold py-3 px-6 rounded-full shadow-sm transition-colors" data-id="element-1242" data-mp="2p6y7qegq6u0v">
                Aanmelden
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-blue-900 text-white py-12 px-4" data-id="element-1243" data-mp="166yyrkyax1r6">
        <div className="max-w-4xl mx-auto text-sm leading-relaxed text-center space-y-6" data-id="element-1244" data-mp="119qi5ossx5s1">
          <p data-id="element-1245" data-mp="1fxtq33qemrjh">
            Meta Advertising Disclaimer: Deze website maakt gebruik van Meta
            Pixel voor advertentiedoeleinden. Dit betekent dat informatie over
            uw bezoek aan deze website wordt gedeeld met Meta (Facebook) om
            gerichte advertenties te kunnen tonen. Door gebruik te maken van
            deze website gaat u akkoord met het gebruik van cookies en
            vergelijkbare technologieën voor analytische en
            advertentiedoeleinden. Deze pagina is een advertorial voor
            Stressless Dogs. Stressless Dogs is een onafhankelijk bedrijf en is
            op geen enkele wijze verbonden aan of onderdeel van Meta (voorheen
            Facebook). Deze advertentie wordt getoond via het Meta
            advertentieplatform, maar Meta is niet verantwoordelijk voor de
            inhoud of diensten aangeboden door Stressless Dogs.
          </p>
          <div className="border-t border-blue-800 pt-6" data-id="element-1246" data-mp="2fpp3d9s10gv5">
            <p className="text-xs tracking-wide" data-id="element-1247" data-mp="2fo48fj1suvwr">
              THIS IS AN ADVERTISEMENT AND NOT AN ACTUAL NEWS ARTICLE, BLOG, OR
              CONSUMER PROTECTION UPDATE
            </p>
          </div>
        </div>
      </footer>

      {/* Sticky bottom bar with countdown + spots + CTA */}
      <StickyCountdownBar ctaHref={ctaHref} data-id="element-1248" data-mp="1s27fhpbvu2xg" />
    </div>;
}